import styled from '@emotion/styled'
import { Site } from '@pubstack/common/src/site'
import { AdmSiteConfig } from '@pubstack/common/src/siteConfig'
import { TamConfig } from '@pubstack/common/src/tam/tamConfig'
import { FunctionComponent, useEffect, useState } from 'react'
import { FieldErrors, SubmitHandler, useForm } from 'react-hook-form'
import { Colors } from '~/assets/style/colors'
import { useScopePermissions } from '~/auth/useScopePermissions'
import Button from '~/components/Button'
import { ContentCard } from '~/components/ContentCard'
import { IllustrationMessage } from '~/components/IllustrationMessage'
import { Input } from '~/components/Input'
import { Skeleton } from '~/components/Skeleton'
import { Status } from '~/components/Status'
import { Toggle } from '~/components/Toggle'
import { Tooltip } from '~/components/Tooltip'
import Table, { handleTableSort, onColumnSort, TableColumns } from '~/components/table/Table'
import TableCell from '~/components/table/TableCell'
import TableRow from '~/components/table/TableRow'
import { ModuleHeader } from '~/feature-components/ModuleHeader'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding: 1.25rem 0;
`

const PubIdForm = styled.form`
  display: inline-flex;
  align-items: center;
  gap: 16px;
`
const SiteGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`
const SiteTitle = styled(TableCell)`
  font-weight: bold;
`
type PureAdmTamIntegrationPageProps = {
  tamConfig: TamConfig
  onCreatePublisherId: (publisherId: string) => Promise<void>
  onCreateSiteConfig: (siteConfig: AdmSiteConfig) => Promise<void>
  isLoading: boolean
  baseCDNUrl: string
}

export const UnstyledPureAdmTamIntegrationPage: FunctionComponent<PureAdmTamIntegrationPageProps> = ({ isLoading, tamConfig, onCreatePublisherId, onCreateSiteConfig, baseCDNUrl }) => {
  /* The Publisher ID and which medias send to Amazon are scope-wide auction settings, so a `user` reads
     this screen and an owner changes it — the same split as the Prebid wrapper next door. */
  const { canEdit, disabledReason } = useScopePermissions()
  // `canEdit &&` because the field opens in edit mode when no Publisher ID has been set yet, which for a
  // `user` would be an editable input they cannot save.
  const [isEditing, setIsEditing] = useState<boolean>(canEdit && !tamConfig.apsPubId)
  const [columns, setColumns] = useState<TableColumns<Site>>([
    {
      name: 'Site',
      isSortable: true,
      order: 'ascending',
      attributeSort: 'name',
    },
    {
      name: 'TAM enabled',
      isSortable: false,
    },
  ])
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<TamConfig>({
    defaultValues: tamConfig,
  })

  const onPublisherIdEdit: SubmitHandler<TamConfig> = () => {
    setIsEditing(!isEditing)
  }

  const onPublisherIdSubmit: SubmitHandler<TamConfig> = (formValue) => {
    onCreatePublisherId(formValue.apsPubId)
    setIsEditing(!isEditing)
  }

  const getErrorMessage = (errors: FieldErrors<TamConfig>) => {
    if (errors.apsPubId?.type === 'required') {
      return 'Publisher ID is required'
    }
  }
  const onSiteConfigSubmit: SubmitHandler<AdmSiteConfig> = (formValue) => {
    onCreateSiteConfig(formValue)
  }

  const sortedSites = handleTableSort(columns, tamConfig.sites)
  useEffect(() => {
    // syncing react-hook-form when the fetched config lands; reset() is an external-store write and cannot move into render
    reset(tamConfig)
    // oxlint-disable-next-line react/set-state-in-effect -- same sync: the edit mode follows the config that was just reset into the form
    setIsEditing(canEdit && !tamConfig.apsPubId)
  }, [tamConfig, canEdit])

  return (
    <Container>
      <ModuleHeader
        catalogItemCode={'tam'}
        catalogItemType={'wrapper'}
        isLoading={isLoading}
        status={<Status state={tamConfig.apsPubId ? 'active' : 'inactive'}>{tamConfig.apsPubId ? 'Active' : 'Not used'}</Status>}
        baseCDNUrl={baseCDNUrl}
      />
      <ContentCard color={Colors.Pumpkin}>
        <PubIdForm onSubmit={handleSubmit(onPublisherIdSubmit)}>
          <Input disabled={!isEditing} type={'text'} name={'apsPubId'} rules={{ required: true }} label={'Publisher ID'} control={control} error={getErrorMessage(errors)} />
          <Tooltip title={disabledReason} disabled={canEdit}>
            {!isEditing ? (
              <Button disabled={!canEdit} variant={'primary'} onClick={handleSubmit(onPublisherIdEdit)} trackingLabel={'adm-integrations-tam-publisher-id-edit'}>
                Edit
              </Button>
            ) : (
              <Button disabled={!isDirty || !canEdit} variant={'primary'} onClick={handleSubmit(onPublisherIdSubmit)} trackingLabel={'adm-integrations-tam-publisher-id-save'}>
                Save
              </Button>
            )}
          </Tooltip>
        </PubIdForm>
      </ContentCard>
      <SiteGroup>
        {!isLoading ? (
          tamConfig.apsPubId !== '' && tamConfig.sites.length > 0 ? (
            <Table columns={columns} isLoading={isLoading} onClickHeading={(column) => onColumnSort(columns, column, setColumns)}>
              {sortedSites.map((site) => {
                const siteConfig = tamConfig.siteConfigs.find((s) => site.id === s.siteId) || { siteId: site.id, scopeId: '', isTamEnabled: !!tamConfig.apsPubId }
                return (
                  <TableRow key={site?.id}>
                    <SiteTitle>{site.name}</SiteTitle>
                    <TableCell>
                      {/* The reason sits on a wrapping span rather than the toggle: a disabled control
                          dispatches no hover of its own. Same shape as the media's own TAM switch. */}
                      <Tooltip title={disabledReason} disabled={canEdit}>
                        <span>
                          <Toggle
                            id={site.id}
                            disabled={!canEdit}
                            value={siteConfig?.isTamEnabled === undefined ? !!tamConfig.apsPubId : siteConfig?.isTamEnabled}
                            onClick={(event) => {
                              event.preventDefault()
                              event.stopPropagation()
                              const data = siteConfig as AdmSiteConfig
                              data.isTamEnabled = !data?.isTamEnabled
                              onSiteConfigSubmit(data)
                            }}
                            trackingLabel={siteConfig?.isTamEnabled ? 'adm-integrations-tam-unset' : 'adm-integrations-tam-set'}
                          />
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                )
              })}
            </Table>
          ) : (
            <IllustrationMessage iconName={'night_sky'} iconSize={'190px'} title={'You don’t have any sites yet or your Publisher Id is not set.'} />
          )
        ) : (
          <Skeleton bigger width={'100%'} />
        )}
      </SiteGroup>
    </Container>
  )
}

export const PureAdmTamIntegrationPage = styled(UnstyledPureAdmTamIntegrationPage)``
